import { useCallback, useState } from 'react';

import {
  skipToken as apolloSkipToken,
  useMutation as useApolloMutation,
  useSuspenseQuery as useApolloSuspenseQuery,
} from '@apollo/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { AutoCompleteCompleteEvent } from 'primereact/autocomplete';

import useDebounce from '@/components/hooks/useDebounce';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  DONORS,
  EMBRYOS,
  GET_CALVES,
  RECIPIENTS,
  SEMEN,
  SHIPMENT,
  SHIPPING_ACCOUNT_DETAILS,
  type SHIPPING_ACCOUNT_DETAILSQuery,
  type SHIPPING_ACCOUNT_DETAILSQueryVariables,
  SHIPPING_ACCOUNT_SEARCH,
  SHIPPING_EQUIPMENT_SEARCH,
  SHIPPING_SET_IN_PROGRESS,
  SHIPPING_SITE_SEARCH,
  SHIPPING_SITES,
  UPDATE_SHIPMENT,
} from '@/types.gql';

import {
  accountDetailsOptions,
  editShipments,
  searchAccountsOptions,
  searchEquipmentOptions,
  searchSitesOptions,
  setInProgress as setInProgressRest,
  shipmentCalvesOptions,
  shipmentCaneSuggestionOptions,
  shipmentDonorsOptions,
  shipmentEmbryosOptions,
  shipmentOptions,
  shipmentRecipientsOptions,
  shipmentSemenOptions,
  shippingSitesOptions,
  useEditShipments,
} from './index';
import type {
  AccountDetail,
  AccountSearchResult,
  EditShipmentInput,
  EquipmentRef,
  SetInProgressResponse,
  ShipmentCalf,
  ShipmentCaneSuggestion,
  ShipmentDetail,
  ShipmentDonor,
  ShipmentEmbryo,
  ShipmentListItem,
  ShipmentRecipient,
  ShipmentSemen,
  SiteRef,
} from './types';

const FLAG = '29181_shipping_graphql_to_rest';

export function useShippingSites() {
  const isRest = useFeatureFlag(FLAG);

  const gqlQuery = useApolloSuspenseQuery(
    SHIPPING_SITES,
    isRest ? apolloSkipToken : {},
  );

  const restQuery = useQuery({
    ...shippingSitesOptions(),
    enabled: isRest,
  });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isLoading,
    };
  }
  return {
    data: gqlQuery.data?.sites?.items as SiteRef[] | undefined,
    isLoading: false,
  };
}

export function useShipmentAccountDetails(
  accountId: string | null | undefined,
) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!accountId;

  const gqlQuery = useApolloSuspenseQuery<
    SHIPPING_ACCOUNT_DETAILSQuery,
    SHIPPING_ACCOUNT_DETAILSQueryVariables
  >(
    SHIPPING_ACCOUNT_DETAILS,
    isRest || !enabled
      ? apolloSkipToken
      : { variables: { accountId: accountId as string } },
  );

  const restQuery = useQuery({
    ...accountDetailsOptions(accountId as string),
    enabled: isRest && enabled,
  });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: false,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.account as AccountDetail | undefined,
    isLoading: false,
    error: gqlQuery.error,
  };
}

function useShipmentSearch<T>(
  gqlQuery: Parameters<typeof useApolloSuspenseQuery>[0],
  restQueryOptions: (
    query: string,
  ) => UseQueryOptions<T[], Error, T[], string[]>,
) {
  const isRest = useFeatureFlag(FLAG);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery);

  const gqlResult = useApolloSuspenseQuery(
    gqlQuery,
    !isRest && debouncedSearch
      ? { variables: { query: debouncedSearch } }
      : apolloSkipToken,
  );

  const { data: restData } = useQuery({
    ...restQueryOptions(debouncedSearch),
    enabled: isRest && !!debouncedSearch,
  });

  const filteredResults = isRest
    ? debouncedSearch
      ? restData
      : undefined
    : (gqlResult.data as { results?: { items?: T[] } } | undefined)?.results
        ?.items;

  const search = useCallback((e: AutoCompleteCompleteEvent) => {
    setSearchQuery(e.query);
  }, []);

  const setFilteredResults = useCallback((value: T[] | undefined) => {
    if (value === undefined) setSearchQuery('');
  }, []);

  return { filteredResults, search, setFilteredResults };
}

export function useShipmentEquipmentSearch() {
  return useShipmentSearch<EquipmentRef>(
    SHIPPING_EQUIPMENT_SEARCH,
    searchEquipmentOptions,
  );
}

export function useShipmentSiteSearch() {
  return useShipmentSearch<SiteRef>(SHIPPING_SITE_SEARCH, searchSitesOptions);
}

export function useShipmentAccountSearch() {
  return useShipmentSearch<AccountSearchResult>(
    SHIPPING_ACCOUNT_SEARCH,
    searchAccountsOptions,
  );
}

export function useUpdateShipment() {
  const isRest = useFeatureFlag(FLAG);
  const { mutateAsync: editShipments } = useEditShipments();
  const [gqlMutate] = useApolloMutation(UPDATE_SHIPMENT);

  const mutateAsync = async (
    rows: ShipmentListItem[],
    updates: Record<string, unknown>,
  ): Promise<
    Array<{ success: boolean; code: number; message: string | null }>
  > => {
    if (isRest) {
      const inputs: EditShipmentInput[] = rows.map((row) => ({
        shipmentId: row.id,
        ...(updates as Omit<EditShipmentInput, 'shipmentId'>),
      }));
      return editShipments(inputs).then((results) =>
        results.map((r) => ({ ...r, message: r.message ?? null })),
      );
    }
    return Promise.all(
      rows.map(async (row) => {
        try {
          const result = await gqlMutate({
            variables: { id: row.id, input: updates },
          });
          return {
            success: !!result.data?.updateShipment,
            code: 200,
            message: null,
          };
        } catch (err: unknown) {
          return {
            success: false,
            code: 400,
            message: err instanceof Error ? err.message : 'Update failed',
          };
        }
      }),
    );
  };

  return { mutateAsync };
}

export function useShipmentDetail(id: string | null) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!id;

  const gqlQuery = useApolloSuspenseQuery(
    SHIPMENT,
    isRest || !enabled
      ? apolloSkipToken
      : { variables: { shipmentId: id! }, fetchPolicy: 'network-only' },
  );

  const restQuery = useQuery({
    ...shipmentOptions(id!),
    enabled: isRest && enabled,
  });

  if (isRest) {
    return {
      data: restQuery.data as ShipmentDetail | undefined,
      refetch: restQuery.refetch,
    };
  }
  return {
    data: gqlQuery.data?.shipment as ShipmentDetail | null | undefined,
    refetch: gqlQuery.refetch,
  };
}

export function useShipmentEmbryos(id: string | null) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!id;

  const gqlQuery = useApolloSuspenseQuery(
    EMBRYOS,
    isRest || !enabled
      ? apolloSkipToken
      : { variables: { shipmentId: id! }, fetchPolicy: 'network-only' },
  );

  const restQuery = useQuery({
    ...shipmentEmbryosOptions(id!),
    enabled: isRest && enabled,
  });

  if (isRest) {
    return {
      data: restQuery.data as ShipmentEmbryo[] | undefined,
      refetch: restQuery.refetch,
    };
  }
  return {
    data: gqlQuery.data?.embryos?.items as ShipmentEmbryo[] | undefined,
    refetch: gqlQuery.refetch,
  };
}

export function useShipmentCaneSuggestion(id: string | null, enabled = true) {
  const isCaneSuggestionEnabled = useFeatureFlag(
    'Recane_Unique_Cane_Suggestion',
  );
  const queryEnabled = enabled && isCaneSuggestionEnabled && !!id;
  const restQuery = useQuery({
    ...shipmentCaneSuggestionOptions(id!),
    enabled: queryEnabled,
  });

  if (!queryEnabled) {
    return {
      data: undefined,
      isLoading: false,
      error: null,
    };
  }

  return {
    data: restQuery.data as ShipmentCaneSuggestion | undefined,
    isLoading: restQuery.isLoading,
    error: restQuery.error,
  };
}

export function useShipmentSemen(id: string | null) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!id;

  const gqlQuery = useApolloSuspenseQuery(
    SEMEN,
    isRest || !enabled
      ? apolloSkipToken
      : { variables: { shipmentId: id! }, fetchPolicy: 'network-only' },
  );

  const restQuery = useQuery({
    ...shipmentSemenOptions(id!),
    enabled: isRest && enabled,
  });

  if (isRest) {
    return {
      data: restQuery.data as ShipmentSemen[] | undefined,
      refetch: restQuery.refetch,
    };
  }
  return {
    data: gqlQuery.data?.semen?.items as ShipmentSemen[] | undefined,
    refetch: gqlQuery.refetch,
  };
}

export function useShipmentDonors(id: string | null) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!id;

  const gqlQuery = useApolloSuspenseQuery(
    DONORS,
    isRest || !enabled
      ? apolloSkipToken
      : { variables: { shipmentId: id! }, fetchPolicy: 'network-only' },
  );

  const restQuery = useQuery({
    ...shipmentDonorsOptions(id!),
    enabled: isRest && enabled,
  });

  if (isRest) {
    return { data: restQuery.data as ShipmentDonor[] | undefined };
  }
  return { data: gqlQuery.data?.donors?.items as ShipmentDonor[] | undefined };
}

export function useShipmentRecipients(id: string | null) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!id;

  const gqlQuery = useApolloSuspenseQuery(
    RECIPIENTS,
    isRest || !enabled
      ? apolloSkipToken
      : { variables: { shipmentId: id! }, fetchPolicy: 'network-only' },
  );

  const restQuery = useQuery({
    ...shipmentRecipientsOptions(id!),
    enabled: isRest && enabled,
  });

  if (isRest) {
    return { data: restQuery.data as ShipmentRecipient[] | undefined };
  }
  return {
    data: gqlQuery.data?.recipients?.items as ShipmentRecipient[] | undefined,
  };
}

export function useShipmentCalves(id: string | null) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!id;

  const gqlQuery = useApolloSuspenseQuery(
    GET_CALVES,
    isRest || !enabled
      ? apolloSkipToken
      : { variables: { shipmentId: id! }, fetchPolicy: 'network-only' },
  );

  const restQuery = useQuery({
    ...shipmentCalvesOptions(id!),
    enabled: isRest && enabled,
  });

  if (isRest) {
    return { data: restQuery.data as ShipmentCalf[] | undefined };
  }
  return { data: gqlQuery.data?.calves?.items as ShipmentCalf[] | undefined };
}

export function useSaveShipment() {
  const isRest = useFeatureFlag(FLAG);
  const [gqlMutate] = useApolloMutation(UPDATE_SHIPMENT);

  const mutateAsync = async (
    shipmentId: string,
    input: Omit<EditShipmentInput, 'shipmentId'>,
  ): Promise<void> => {
    if (isRest) {
      const results = await editShipments([{ shipmentId, ...input }]);
      const failed = results.find((r) => !r.success);
      if (failed) throw new Error(failed.message ?? 'Update failed');
    } else {
      const result = await gqlMutate({ variables: { id: shipmentId, input } });
      if (!result.data?.updateShipment) throw new Error('Update failed');
    }
  };

  return { mutateAsync };
}

export function useSetInProgress() {
  const isRest = useFeatureFlag(FLAG);
  const refreshAdditionalData = useFeatureFlag(
    'Shipping_Additional_Data_Local_Time',
  );
  const queryClient = useQueryClient();
  const [gqlMutate] = useApolloMutation(SHIPPING_SET_IN_PROGRESS);

  const mutateAsync = async (
    shipmentId: string,
    packedById?: string | null,
  ): Promise<Pick<SetInProgressResponse, 'shipmentStatus' | 'packedById'>> => {
    if (isRest) {
      const result = await setInProgressRest(shipmentId, packedById);
      if (refreshAdditionalData) {
        await queryClient.invalidateQueries({
          queryKey: shipmentOptions(shipmentId).queryKey,
        });
      }
      return result;
    }
    const result = await gqlMutate({
      variables: { shipmentId, packedById: packedById ?? null },
    });
    return result.data?.shipping_setInProgress ?? {};
  };

  return { mutateAsync };
}
